import { useState } from "react";
import { Building2, Crown, Cake, PartyPopper, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Panel, Tag } from "@/components/kit";
import { TODAY } from "@/data/mock";
import { useStore } from "@/store/AppStore";
import { upcomingBirthdays } from "@/lib/birthdays";
import { fmtDate } from "@/lib/format";

const KIND = { empresa: ["Aniversário da empresa", Building2], empresaria: ["Aniversário da empresária", Crown], colaboradora: ["Aniversário", Cake] };
const when = (d) => (d === 0 ? "Hoje!" : d === 1 ? "Amanhã" : `em ${d} dias`);

const todayList = (state, ids) => upcomingBirthdays(state, ids, 0);
const wish = (i, selfId) => {
  if (i.id === selfId) return `Feliz aniversário, ${i.name.split(" ")[0]}! Toda a equipe deseja um dia cheio de alegria, saúde e conquistas.`;
  if (i.kind === "empresa") return `Hoje a ${i.name} completa ${i.years} anos! Parabéns a todas que fazem parte dessa história.`;
  return `Hoje é aniversário de ${i.name}. Não esqueça de deixar os parabéns!`;
};

export function BirthdayGreeting({ companyId, selfId, role }) {
  const { state } = useStore();
  const key = `bella-bday-${role}-${TODAY}`;
  const list = todayList(state, [companyId]);
  const [open, setOpen] = useState(() => list.length > 0 && !sessionStorage.getItem(key));
  const close = () => { sessionStorage.setItem(key, "1"); setOpen(false); };
  const mine = list.find((i) => i.id === selfId);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="w-[calc(100%-2rem)] overflow-hidden rounded-2xl border-gold/40 p-0 sm:max-w-md [&>button]:text-white [&>button]:opacity-90" data-testid="birthday-greeting">
        <div className="relative bg-neutral-900 px-8 pb-8 pt-10 text-center text-white">
          <Sparkles className="absolute left-6 top-6 h-5 w-5 animate-pulse text-gold" />
          <Sparkles className="absolute bottom-6 right-8 h-4 w-4 animate-pulse text-gold" />
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold"><PartyPopper className="h-8 w-8 text-neutral-900" /></span>
          <DialogTitle className="mt-5 font-display text-3xl font-semibold text-gold">{mine ? "Feliz aniversário!" : "Dia de comemorar!"}</DialogTitle>
          <DialogDescription className="mt-2 text-sm text-neutral-300">{fmtDate(TODAY)}</DialogDescription>
        </div>
        <ul className="space-y-3 px-8 py-6">
          {list.map((i) => (
            <li key={i.id} data-testid={`birthday-greeting-${i.id}`} className="flex gap-3 text-sm text-neutral-700"><Cake className="mt-0.5 h-4 w-4 shrink-0 text-gold" />{wish(i, selfId)}</li>
          ))}
        </ul>
        <div className="px-8 pb-8"><Button className="w-full font-semibold" onClick={close} data-testid="birthday-greeting-close">Obrigada!</Button></div>
      </DialogContent>
    </Dialog>
  );
}

export function TodayBirthdayBanner({ companyIds }) {
  const { state } = useStore();
  const list = todayList(state, companyIds);
  if (!list.length) return null;
  return (
    <div data-testid="today-birthday-banner" className="mb-8 flex flex-col gap-4 rounded-xl bg-neutral-900 p-6 text-white sm:flex-row sm:items-center">
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold"><PartyPopper className="h-6 w-6 text-neutral-900" /></span>
      <div>
        <p className="font-display text-xl font-semibold text-gold">Aniversariantes de hoje</p>
        <p className="mt-1 text-sm text-neutral-300">{list.map((i) => `${i.name}${i.kind === "empresa" ? ` (${i.years} anos)` : ` · ${i.company}`}`).join("  •  ")}</p>
      </div>
    </div>
  );
}

export function BirthdayPanel({ companyIds, title = "Próximos aniversários", className = "", showCompany }) {
  const { state } = useStore();
  const list = upcomingBirthdays(state, companyIds);
  return (
    <Panel title={title} subtitle="Lembretes dos próximos 30 dias" className={className} testId="birthday-panel">
      {!list.length && <p className="text-sm text-neutral-400">Nenhum aniversário nos próximos 30 dias.</p>}
      <ul className="divide-y divide-neutral-100">
        {list.map((i) => {
          const [label, Icon] = KIND[i.kind];
          return (
            <li key={i.id} data-testid={`birthday-${i.id}`} className={`flex items-center gap-3 py-3 ${i.days === 0 ? "-mx-3 rounded-lg bg-gold-soft px-3" : ""}`}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold-soft"><Icon className="h-4 w-4 text-gold-dark" /></span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{i.name}</p>
                <p className="text-xs text-neutral-500">{label}{i.kind === "empresa" ? ` · ${i.years} anos` : ""}{showCompany && i.kind !== "empresa" ? ` · ${i.company}` : ""} · {fmtDate(i.date)}</p>
              </div>
              {i.days === 0 ? <span data-testid={`birthday-when-${i.id}`} className="rounded-full bg-gold px-2.5 py-0.5 text-[11px] font-semibold text-neutral-900">Hoje!</span> : <Tag tone={i.days <= 7 ? "dark" : "muted"} testId={`birthday-when-${i.id}`}>{when(i.days)}</Tag>}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
